/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "How to migrate to Hightouch Events", description = "Learn how to migrate to Hightouch Events from another event collection solution.", lastUpdatedDate = new Date(1782345600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "How to migrate to Hightouch Events",
        description: "Learn how to migrate to Hightouch Events from another event collection solution.",
        lastUpdatedDate: "Thu Jun 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What you'll learn",
          "depth": 0,
          "id": "what-youll-learn"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      strong: "strong"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "This guide walks through migrating from your current event tracking tool, such as Segment or RudderStack, to Hightouch Events. It covers how to plan the migration, run both systems in parallel, validate the data, and cut over your destinations — keeping your existing workflows running throughout. The steps below follow that order."
      }), "\n", _jsx(_components.p, {
        children: "If you're coming from a specific platform, read the platform guide alongside this roadmap for the details that differ: Segment, RudderStack, mParticle, or Amplitude."
      }), "\n", _jsxs(_components.p, {
        children: ["Before you start, make the foundational decisions in ", _jsx(_components.a, {
          href: "/events/implementation-plan",
          children: "Plan your Events implementation"
        }), " — your event model, identity, contract strategy, coverage, and delivery. Those apply to any implementation; this guide covers what's specific to a migration. For how Events works and where your data lives, see the ", _jsx(_components.a, {
          href: "/events/overview",
          children: "Events overview"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "what-youll-learn",
        children: "What you'll learn"
      }), "\n", _jsx(_components.p, {
        children: "Follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "preparing-for-migration",
              children: "Preparing for migration"
            })
          }), ": Scope your migration, decide on your data model and tracking plan, and estimate the time and resources required."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "implementing-hightouch-events",
              children: "Implementing Hightouch Events SDKs"
            })
          }), ": Switch to Hightouch SDKs with an analytics wrapper function to support validation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "validating-your-migration",
              children: "Validating your migration"
            })
          }), ": Verify your setup and ensure data quality matches or exceeds your previous platform."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "unifying-data",
              children: "Unifying historical and new data"
            })
          }), ": Deduplicate and unify your historical data with new events collected by Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "updating-downstream-applications",
              children: "Updating downstream applications and destinations"
            })
          }), ": Configure or set up the applications that consume your event data."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You'll also set up ", _jsx(_components.a, {
          href: "/events/contracts/management",
          children: "data contracts"
        }), " to keep your event data consistent as it flows in, and ", _jsx(_components.a, {
          href: "/events/monitoring",
          children: "monitoring"
        }), " to catch volume drops, violations, and sync errors. Define contracts as you instrument your events, and turn on monitoring before you start shifting traffic."]
      }), "\n", _jsx(_components.p, {
        children: "Each step keeps your existing data flowing while you move collection to Hightouch Events, so you can validate the new setup before you turn off your current tool."
      })]
    });
  }
}
export default MDXContent;
